import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Card, Tab, Tabs, Typography } from '@mui/material';
import { alpha } from '@mui/system';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { ListEntityType } from 'src/services/lists/lists.types';

import ContactsList from 'src/sections/contacts/table/contacts-list';
import ListsTableView from 'src/sections/lists/lists-table-view';

import { TenantType } from 'src/types/enums';

enum TabValue {
  CONTACTS = 'contacts',
  LISTS = 'lists',
}

export default function ContactsListView() {
  const authContext = useAuthContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTab, setSelectedTab] = useState<TabValue | undefined>();

  const activeTabs = useMemo(() => {
    switch (authContext.organization?.type) {
      case TenantType.Recruiter:
      case TenantType.Client:
        return [TabValue.CONTACTS, TabValue.LISTS];
      default:
        return [TabValue.CONTACTS];
    }
  }, [authContext.organization?.type]);

  // Set default tab
  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (selectedTab) return;

    let defaultTab: TabValue = TabValue.CONTACTS;

    const existingTab = searchParams.get('tab');
    if (existingTab && Object.values(TabValue).includes(existingTab as TabValue) && activeTabs.includes(existingTab as TabValue)) {
      defaultTab = existingTab as TabValue;
    }

    setSelectedTab(defaultTab);
  }, [selectedTab, searchParams, activeTabs]);

  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (!selectedTab) return;

    setSearchParams({ tab: selectedTab }, { replace: true });
  }, [selectedTab, setSearchParams]);

  return (
    <Card>
      <Tabs
        value={selectedTab}
        onChange={(_event, tab) => setSelectedTab(tab)}
        sx={{
          px: 2.5,
          boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
        }}
      >
        {activeTabs.map((tab) => (
          <Tab
            key={tab}
            iconPosition="end"
            value={tab}
            label={
              <Typography variant="subtitle2" sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}>
                {tab}
              </Typography>
            }
          />
        ))}
      </Tabs>

      {selectedTab === TabValue.CONTACTS && <ContactsList />}

      {selectedTab === TabValue.LISTS && <ListsTableView entityType={ListEntityType.CONTACT} />}
    </Card>
  );
}
